import React, { useState, useCallback } from 'react';
import { DialogContainer, Dialog, Heading, Content, ButtonGroup, Button, Text } from "@react-spectrum/s2";
import { Trans, useTranslation } from 'react-i18next';
import { APP_NAME } from '../../constants';

interface TermsDialogProps {
  onAccept: () => void;
  onDecline: () => void;
}

const sectionStyle: React.CSSProperties = { marginBottom: 12 };
const headerStyle: React.CSSProperties = { fontWeight: 'bold', marginBottom: 4 };
const subHeaderStyle: React.CSSProperties = { fontWeight: 'bold', marginBottom: 8 };
const definitionStyle: React.CSSProperties = { marginBottom: 8 };
const linkStyle: React.CSSProperties = { color: 'var(--color-accent)', textDecoration: 'underline' };

export const TermsDialog: React.FC<TermsDialogProps> = ({ onAccept, onDecline }) => {
  const { t } = useTranslation();
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 10;
    if (isAtBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  }, [hasScrolledToEnd]);

  return (
    <DialogContainer onDismiss={() => {}}>
      <Dialog isDismissible={false}>
        <Heading>{t('settings.terms.title')}</Heading>

        <Content>
          <Text>
            {t('settings.terms.intro', { appName: APP_NAME })}
          </Text>

          <div
            style={{ marginTop: 16, padding: 16, background: 'var(--spectrum-global-color-gray-100)', borderRadius: 'var(--radius-400)', maxHeight: 300, overflowY: 'auto', fontSize: 13, lineHeight: 1.5 }}
            onScroll={handleScroll}
          >
          <div style={{ ...headerStyle, fontSize: 14 }}>{t('settings.terms.companyName')}</div>
          <div style={{ ...headerStyle, fontSize: 14, marginBottom: 16 }}>{t('settings.terms.agreementTitle')}</div>
          <p style={sectionStyle}>{t('settings.terms.preamble')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading1')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_3" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_4" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_5" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_6" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_7" components={{ strong: <strong />, anchor: <a href="https://www.adobe.com/go/adobe-gen-ai-user-guidelines" target="_blank" rel="noopener noreferrer" style={linkStyle} /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_8" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_9" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_10" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_11" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_12" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_13" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_1_14" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading2')}</div>
          <p style={sectionStyle}>
            <Trans i18nKey="settings.terms.body2" components={{ anchor: <a href="http://www.adobe.com/go/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle} /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading3')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_3_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_3_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_3_3" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading4')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_3" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_4" components={{ strong: <strong />, anchor: <a href="http://www.adobe.com/go/machine_learning" target="_blank" rel="noopener noreferrer" style={linkStyle} /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_5" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_6" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_7" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_8" components={{ strong: <strong />, anchor: <a href="https://www.adobe.com/go/restricted_fonts" target="_blank" rel="noopener noreferrer" style={linkStyle} /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_4_9" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading5')}</div>
          <p style={sectionStyle}>{t('settings.terms.body5')}</p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_5_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_5_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_5_3" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_5_3_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_5_3_2" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading6')}</div>
          <p style={sectionStyle}>{t('settings.terms.body6')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading7')}</div>
          <p style={sectionStyle}>{t('settings.terms.body7')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading8')}</div>
          <p style={sectionStyle}>{t('settings.terms.body8')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading9')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_9_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_9_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_9_3" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading10')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_10_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_10_2" components={{ strong: <strong /> }} />
          </p>
          <div style={subHeaderStyle}>{t('settings.terms.heading11')}</div>
          <p style={sectionStyle}>{t('settings.terms.body11')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading12')}</div>
          <p style={sectionStyle}>{t('settings.terms.body12')}</p>
          <div style={subHeaderStyle}>{t('settings.terms.heading13')}</div>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_1" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_2" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_3" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_4" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_5" components={{ strong: <strong /> }} />
          </p>
          <p style={definitionStyle}>
            <Trans i18nKey="settings.terms.clause_13_6" components={{ strong: <strong /> }} />
          </p>
          <p style={{ marginTop: 16, fontWeight: 'bold' }}>{t('settings.terms.acknowledge')}</p>
          </div>
      </Content>
        <ButtonGroup>
          <Button variant="secondary" onPress={onDecline}>
            <Text>{t('settings.terms.decline')}</Text>
          </Button>
          <Button
            variant="accent"
            onPress={onAccept}
            isDisabled={!hasScrolledToEnd}
            autoFocus={hasScrolledToEnd}
          >
            <Text>{hasScrolledToEnd ? t('settings.terms.accept') : t('settings.terms.scrollToRead')}</Text>
          </Button>
        </ButtonGroup>
      </Dialog>
    </DialogContainer>
  );
};
